import { useEffect, useState } from "react";
import { Loader2, ImagePlus, X, Video } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useShop } from "../context/ShopContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const emptyForm = {
  name_tr: "",
  name_en: "",
  series: "wood",
  dims: "",
  price: "",
  description_tr: "",
  description_en: "",
  image_url: "",
  bestseller: false,
};

const ProductForm = ({ open, onOpenChange, product, onSaved }) => {
  const { t, lang, productMedia, options } = useShop();
  const d = t.adminProducts;
  const seriesOpts = options?.series || [];
  const dimsOpts = options?.dims || [];
  const editing = Boolean(product);
  const [form, setForm] = useState(emptyForm);
  const [dimsSelected, setDimsSelected] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [video, setVideo] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (product) {
        setForm({
          name_tr: product.name?.tr || "",
          name_en: product.name?.en || "",
          series: product.series || "wood",
          dims: product.dims || "",
          price: String(product.price ?? ""),
          description_tr: product.description?.tr || "",
          description_en: product.description?.en || "",
          image_url: product.image_url || "",
          bestseller: Boolean(product.bestseller),
        });
        setDimsSelected(
          product.available_dims?.length
            ? [...product.available_dims]
            : [product.dims].filter(Boolean)
        );
        setPhotoPreview(productMedia(product) || null);
      } else {
        setForm(emptyForm);
        setDimsSelected([]);
        setPhotoPreview(null);
      }
      setPhoto(null);
      setVideo(null);
    }
  }, [open, product]);

  const toggleDims = (value) =>
    setDimsSelected((arr) =>
      arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
    );

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    if (photoPreview && photoPreview.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(
          `${API}/admin/products/${product.id}`,
          {
            name_tr: form.name_tr,
            name_en: form.name_en,
            series: form.series,
            dims: form.dims,
            price: Number(form.price),
            description_tr: form.description_tr,
            description_en: form.description_en,
            image_url: form.image_url,
            bestseller: form.bestseller,
            available_dims: dimsSelected.length ? dimsSelected : [form.dims],
          },
          { withCredentials: true }
        );
        if (photo) await uploadMedia(product.id, "photo", photo);
        if (video) await uploadMedia(product.id, "video", video);
      } else {
        const data = new FormData();
        Object.entries({ ...form, price: String(Number(form.price)) }).forEach(([k, v]) =>
          data.append(k, String(v))
        );
        (dimsSelected.length ? dimsSelected : [form.dims]).forEach((v) =>
          data.append("available_dims", v)
        );
        if (photo) data.append("photo", photo);
        if (video) data.append("video", video);
        await axios.post(`${API}/admin/products`, data, { withCredentials: true });
      }
      toast.success(d.saved);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : d.error);
    } finally {
      setSaving(false);
    }
  };

  const uploadMedia = (id, kind, file) => {
    const data = new FormData();
    data.append("kind", kind);
    data.append("file", file);
    return axios.post(`${API}/admin/products/${id}/media`, data, { withCredentials: true });
  };

  const labelCls = "text-[11px] tracking-[0.25em] uppercase text-brown-muted";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-cream border-brown/10 text-brown sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="product-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-medium text-brown">
            {editing ? d.editTitle : d.newTitle}
          </DialogTitle>
          <DialogDescription className="text-sm font-light text-brown-light">
            {d.sub}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-7" data-testid="product-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className={labelCls} htmlFor="pf-name-tr">{d.nameTr}</label>
              <Input id="pf-name-tr" required minLength={2} value={form.name_tr} onChange={set("name_tr")} className="editorial-input mt-1" data-testid="pf-name-tr" />
            </div>
            <div>
              <label className={labelCls} htmlFor="pf-name-en">{d.nameEn}</label>
              <Input id="pf-name-en" required minLength={2} value={form.name_en} onChange={set("name_en")} className="editorial-input mt-1" data-testid="pf-name-en" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <label className={labelCls} htmlFor="pf-series">{d.series}</label>
              <select
                id="pf-series"
                value={form.series}
                onChange={set("series")}
                className="mt-2 w-full bg-transparent border border-brown/20 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-olive cursor-pointer"
                data-testid="pf-series"
              >
                {seriesOpts.map((o) => (
                  <option key={o.key} value={o.key}>
                    {lang === "tr" ? o.label_tr : o.label_en}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="pf-dims">{d.dims}</label>
              <Input id="pf-dims" required placeholder={d.dimsPlaceholder} value={form.dims} onChange={set("dims")} className="editorial-input mt-1" data-testid="pf-dims" />
            </div>
            <div>
              <label className={labelCls} htmlFor="pf-price">{d.price}</label>
              <Input id="pf-price" required type="number" min={0} step="0.01" value={form.price} onChange={set("price")} className="editorial-input mt-1" data-testid="pf-price" />
            </div>
          </div>

          <div>
            <p className={`${labelCls} mb-3`}>{d.availableDims}</p>
            <div className="flex flex-wrap gap-2" data-testid="pf-dims-checkboxes">
              {dimsOpts.map((value) => {
                const active = dimsSelected.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleDims(value)}
                    className={`px-4 py-2 rounded-full text-xs font-medium tracking-wide transition-colors duration-300 border ${
                      active
                        ? "bg-olive text-cream border-olive"
                        : "border-brown/20 text-brown-light hover:border-olive hover:text-olive-dark"
                    }`}
                    data-testid={`pf-dims-check-${value.replace(/[^0-9]/g, "-")}`}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-brown-muted font-light">{d.availableDimsHint}</p>
          </div>

          <div>
            <label className={labelCls} htmlFor="pf-desc-tr">{d.descTr}</label>
            <Textarea id="pf-desc-tr" rows={2} maxLength={1000} value={form.description_tr} onChange={set("description_tr")} className="editorial-input mt-1 resize-none" data-testid="pf-desc-tr" />
          </div>
          <div>
            <label className={labelCls} htmlFor="pf-desc-en">{d.descEn}</label>
            <Textarea id="pf-desc-en" rows={2} maxLength={1000} value={form.description_en} onChange={set("description_en")} className="editorial-input mt-1 resize-none" data-testid="pf-desc-en" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className={`${labelCls} mb-2`}>{d.photoLabel}</p>
              {photoPreview ? (
                <div className="relative w-28 h-28 overflow-hidden border border-brown/10 mb-2">
                  <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => {
                      setPhoto(null);
                      setPhotoPreview(editing ? productMedia(product) : null);
                    }}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-brown/80 text-cream flex items-center justify-center"
                    aria-label="Remove photo"
                    data-testid="pf-photo-remove"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-3 px-5 py-3 border border-dashed border-brown/25 text-sm text-brown-light hover:border-olive hover:text-olive-dark cursor-pointer transition-colors duration-300" data-testid="pf-photo-upload">
                  <ImagePlus size={17} strokeWidth={1.5} /> {d.photoLabel}
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
                </label>
              )}
              {!editing && !photo && (
                <Input placeholder={d.imageUrlLabel} value={form.image_url} onChange={set("image_url")} className="editorial-input mt-3" data-testid="pf-image-url" />
              )}
              <p className="mt-2 text-xs text-brown-muted font-light">{d.photoHint}</p>
            </div>
            <div>
              <p className={`${labelCls} mb-2`}>{d.videoLabel}</p>
              <label className="inline-flex items-center gap-3 px-5 py-3 border border-dashed border-brown/25 text-sm text-brown-light hover:border-olive hover:text-olive-dark cursor-pointer transition-colors duration-300" data-testid="pf-video-upload">
                <Video size={17} strokeWidth={1.5} />
                {video ? video.name : d.videoLabel}
                <input
                  type="file"
                  accept="video/mp4,video/webm"
                  className="hidden"
                  onChange={(e) => setVideo(e.target.files?.[0] || null)}
                />
              </label>
              {editing && product?.video_path && !video && (
                <p className="mt-2 text-xs text-olive-dark">{d.hasVideo}</p>
              )}
              <p className="mt-2 text-xs text-brown-muted font-light">{d.videoHint}</p>
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm text-brown cursor-pointer" data-testid="pf-bestseller-label">
            <input
              type="checkbox"
              checked={form.bestseller}
              onChange={set("bestseller")}
              className="w-4 h-4 accent-[#8B9068]"
              data-testid="pf-bestseller"
            />
            {d.bestseller}
          </label>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-brown text-cream text-sm font-medium tracking-wide hover:bg-olive-dark disabled:opacity-60 transition-colors duration-300"
            data-testid="pf-submit"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            {saving ? d.saving : d.save}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductForm;
